import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { MapProvider } from "react-map-gl/mapbox";
import GlobalStoreContext from "../../context/GlobalStoreContext";
import Helper from "../../Helper";
import { CalendarEvent } from "../../models/CalendarEvent";
import { Delivery } from "../../models/Delivery";
import PlotAPI from "../../plotAPI";
import TeamInfo from "../../teamInfo";
import ProjectBanner from "../Project/ProjectBanner";
import DamnContactInformation from "./damnContactInformation";
import DamnHeader from "./damnHeader";
import DamnInformationSection from "./damnInformationSection";
import DamnPhotosSection from "./damnPhotosSection";
import DeliveryTiming from "./deliveryTiming";
import DeliveryVehicleType from "./deliveryVehicleType";
import DamnMapSection from "./Map/damnMapSection";
import ShareETA from "./shareEta";

interface Props {
    delivery_token: string | undefined;
}

const DamnPage: React.FC<Props> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const [delivery, setDelivery] = React.useState<Delivery | null>(null);
    const [calendarEvent, setCalendarEvent] = React.useState<CalendarEvent | null>(null);
    const [gcTeam, setGCTeam] = React.useState<TeamInfo | null>(null);
    const [receivingTeam, setReceivingTeam] = React.useState<TeamInfo | null>(null);

    const getDelivery = () => {
        if (!props.delivery_token) return;

        PlotAPI.getDeliveryByUniqueToken(Helper.currentUrlSubdomain(), props.delivery_token, dayjs.tz.guess()).then(
            (delivery) => {
                if (!delivery || !delivery.project?.timezone || !delivery.project?.uniqueToken) {
                    return;
                }

                const deliveryInCorrectedTimezone = delivery.inAlternateTimezone(delivery.project.timezone);

                setDelivery(deliveryInCorrectedTimezone);

                context.setCurrentProjectToken(delivery.project.uniqueToken);
            }
        );
    };

    useEffect(() => {
        getDelivery();
    }, [props.delivery_token]);

    useEffect(() => {
        if (!delivery || !context.currentProjectInfo || !delivery.calendarEvent) {
            if (calendarEvent) {
                setCalendarEvent(null);
            }

            return;
        }

        PlotAPI.getCalendarEvent(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            delivery.calendarEvent?.id,
            context.currentProjectInfo.timezone
        ).then((calendarEvent) => {
            setCalendarEvent(calendarEvent);
        });
    }, [delivery, context.currentProjectInfo]);

    useEffect(() => {
        if (!context.currentProjectInfo || !calendarEvent) return;

        const team = context
            .teamsForProject(context.currentProjectInfo.unique_token)
            .find((team) => team.id === context.currentProjectInfo?.owning_team_id);

        setGCTeam(team || null);

        const rcvTeam = context
            .teamsForProject(context.currentProjectInfo.unique_token)
            .find((team) => team.id === calendarEvent.deliveryReceivingTeamId());
        setReceivingTeam(rcvTeam || null);
    }, [context.currentProjectInfo, context.teamsForProject, calendarEvent]);

    if (!delivery || !calendarEvent || !context.currentProjectInfo) {
        return <Skeleton variant="rectangular" width={"100%"} height={"100%"} animation="wave" />;
    }

    const saveDelivery = (updatedDelivery: Delivery) => {
        const subdomain = context.currentProjectInfo.claimed_by_team_subdomain;
        const token = context.currentProjectInfo.unique_token;

        setDelivery(updatedDelivery);

        return PlotAPI.updateDelivery(subdomain, token, updatedDelivery);
    };

    return (
        <div className="min-h-screen overflow-y-auto bg-linear-to-br from-gray-50 to-gray-100">
            <ProjectBanner projectInfo={context.currentProjectInfo} />

            <DamnHeader calendarEvent={calendarEvent} delivery={delivery} />
            <main className="pb-6">
                <MapProvider>
                    <DamnMapSection calendarEvent={calendarEvent} />
                </MapProvider>
                <DeliveryTiming calendarEvent={calendarEvent} />
                <ShareETA delivery={delivery} calendarEvent={calendarEvent} saveDelivery={saveDelivery} />
                <DeliveryVehicleType delivery={delivery} saveDelivery={saveDelivery} />
                <DamnPhotosSection calendarEvent={calendarEvent} />
                <DamnInformationSection
                    calendarEvent={calendarEvent}
                    delivery={delivery}
                    gcTeam={gcTeam}
                    receivingTeam={receivingTeam}
                />
                <DamnContactInformation delivery={delivery!} receivingTeam={receivingTeam} />
            </main>
        </div>
    );
};

export default DamnPage;
